import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { API_URL } from "@/shared"
import { showNotification } from "@mantine/notifications"
import {
  FilterRendicionParams,
  FilterRendicionReporteParams,
  CreateRendicionInput,
  CajaChicaAsignacion,
  CreateCajaChicaAsignacionInput,
  UpdateCajaChicaAsignacionInput,
  CicloCajaChica,
  Rendicion,
  RendicionDeclaracionJuradaItem,
  RendicionItem,
  RendicionConsumo,
  RendicionPlanillaMovilidadItem,
  RendicionReporteRow,
  RendicionReporteSummary,
  RendicionSeccion,
  RendicionSustentoTecnicoEntry,
  RendicionTravelRequestOption,
  ComprobanteDecisionEstado,
  getRendicionId
} from "../domain/rendicion.interface"

export const RENDICION_KEYS = {
  GET_ALL: "RENDICION_GET_ALL",
  GET_BY_ID: "RENDICION_GET_BY_ID",
  GET_BY_TRAVEL_REQUEST: "RENDICION_GET_BY_TRAVEL_REQUEST",
  GET_MY_TRAVEL_REQUESTS: "RENDICION_GET_MY_TRAVEL_REQUESTS",
  GET_CAJA_CHICA_ASIGNACIONES: "RENDICION_GET_CAJA_CHICA_ASIGNACIONES"
}

export function useGetCajaChicaAsignaciones(pais: string) {
  return useQuery({
    queryKey: [RENDICION_KEYS.GET_CAJA_CHICA_ASIGNACIONES, pais],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/caja-chica/asignaciones`, { params: { pais } })
      return (response.data.data ?? []) as CajaChicaAsignacion[]
    }
  })
}

export function useCreateCajaChicaAsignacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateCajaChicaAsignacionInput) => {
      const response = await axios.post(`${API_URL}/rendiciones/caja-chica/asignaciones`, data)
      return response.data.data as CajaChicaAsignacion
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_CAJA_CHICA_ASIGNACIONES, variables.pais] })
      showNotification({ title: "Caja chica asignada", message: "La persona ya puede rendir su caja chica", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo asignar", message: error?.response?.data?.message || "Revisa la información e intenta nuevamente", color: "red" })
  })
}

export function useUpdateCajaChicaAsignacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateCajaChicaAsignacionInput & { id: string; pais: string }) => {
      const { id, nombre, cedula, cargo, area, montoAsignado } = input
      const response = await axios.put(`${API_URL}/rendiciones/caja-chica/asignaciones/${id}`, { nombre, cedula, cargo, area, montoAsignado })
      return response.data.data as CajaChicaAsignacion
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_CAJA_CHICA_ASIGNACIONES, variables.pais] })
      showNotification({ title: "Actualizado", message: "La asignación de caja chica se actualizó", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo actualizar", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useDeleteCajaChicaAsignacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; pais: string }) => {
      await axios.delete(`${API_URL}/rendiciones/caja-chica/asignaciones/${id}`)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_CAJA_CHICA_ASIGNACIONES, variables.pais] })
      showNotification({ title: "Quitada", message: "La persona ya no tiene caja chica asignada", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo quitar", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useGetMyTravelRequests(pais?: string) {
  return useQuery({
    queryKey: [RENDICION_KEYS.GET_MY_TRAVEL_REQUESTS, pais],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/mis-viajes`, { params: { pais } })
      return (response.data.data ?? []) as RendicionTravelRequestOption[]
    },
    staleTime: 30_000
  })
}

export function useCreateRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateRendicionInput) => {
      const response = await axios.post(`${API_URL}/rendiciones`, data)
      return response.data.data as Rendicion
    },
    onSuccess: (_rendicion, variables) => {
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_ALL] })
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_MY_TRAVEL_REQUESTS] })
      if (variables.travelRequestId) queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_BY_TRAVEL_REQUEST, variables.travelRequestId] })
      showNotification({ title: "Rendición creada", message: "Ya puedes registrar tus gastos", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo crear", message: error?.response?.data?.message || "Revisa la información e intenta nuevamente", color: "red" })
  })
}

export function useAddRendicionApartados() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, apartados }: { id: string; apartados: RendicionSeccion[] }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/apartados`, { apartados })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
      showNotification({ title: "Categoría agregada", message: "Ya puedes registrar gastos en la nueva categoría", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo agregar", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useGetRendiciones(filter: FilterRendicionParams) {
  return useQuery({
    queryKey: [RENDICION_KEYS.GET_ALL, filter],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/rendiciones`, { params: filter, timeout: 15000 })
        return { data: (response.data.data ?? []) as Rendicion[], totalRecords: (response.data.totalRecords ?? 0) as number }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message = error.code === "ECONNABORTED"
            ? "El servidor no pudo consultar MongoDB. Revisa la conexión o la IP autorizada en Atlas."
            : error.response?.data?.message || error.message
          throw new Error(message)
        }
        throw error
      }
    },
    retry: 0
  })
}

export function useGetRendicion(id: string | undefined) {
  return useQuery({
    queryKey: [RENDICION_KEYS.GET_BY_ID, id],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/${id}`)
      return (response.data.data || response.data) as Rendicion
    },
    enabled: !!id
  })
}

export function useGetRendicionReporte(filter: FilterRendicionReporteParams, enabled = true) {
  return useQuery({
    queryKey: ["RENDICION_REPORTE", filter],
    queryFn: async () => {
      // El ResponseInterceptor del backend, cuando el controller devuelve { data, summary }, sube
      // "summary" al nivel superior de la respuesta (igual que hace con "totalRecords" en la bandeja) —
      // por eso se lee de response.data.summary y no de response.data.data.summary.
      const response = await axios.get(`${API_URL}/rendiciones/reporte`, { params: filter })
      return { data: (response.data.data ?? []) as RendicionReporteRow[], summary: (response.data.summary ?? { total: 0, aprobadas: 0, rechazadas: 0, pendientes: 0, totalLiquidado: 0 }) as RendicionReporteSummary }
    },
    enabled
  })
}

export function useGetRendicionByTravelRequest(travelRequestId: string | undefined) {
  return useQuery({
    queryKey: [RENDICION_KEYS.GET_BY_TRAVEL_REQUEST, travelRequestId],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/by-travel-request/${travelRequestId}`)
      return (response.data.data ?? null) as Rendicion | null
    },
    enabled: !!travelRequestId,
    refetchInterval: (query) => (query.state.data ? false : 4000)
  })
}

export function useDownloadRendicionPdf() {
  return useMutation({
    mutationFn: async ({ id, codigo }: { id: string; codigo: string }) => {
      const response = await axios.get(`${API_URL}/rendiciones/${id}/pdf`, { responseType: "blob" })
      const url = URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = url
      link.download = `${codigo || "rendicion"}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
    onError: () => showNotification({ title: "No se pudo descargar", message: "Intenta nuevamente", color: "red" })
  })
}

export function useDownloadRendicionExcel() {
  return useMutation({
    mutationFn: async ({ id, codigo }: { id: string; codigo: string }) => {
      const response = await axios.get(`${API_URL}/rendiciones/${id}/excel`, { responseType: "blob" })
      const url = URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = url
      link.download = `${codigo || "rendicion"}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
    onError: () => showNotification({ title: "No se pudo descargar", message: "Intenta nuevamente", color: "red" })
  })
}

/** URL para ver un archivo de la rendición: los guardados en SharePoint (graph://) los sirve el back con permiso sobre la rendición. */
export const rendicionArchivoUrl = (url?: string): string | undefined => (url?.startsWith("graph://") ? `${API_URL}/rendiciones/archivo?ref=${encodeURIComponent(url)}` : url)

function invalidateRendicion(queryClient: ReturnType<typeof useQueryClient>, id?: string) {
  queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_ALL] })
  // Si la respuesta no trae el id (llega como _id), se refrescan todos los detalles en lugar de ninguno.
  queryClient.invalidateQueries({ queryKey: id ? [RENDICION_KEYS.GET_BY_ID, id] : [RENDICION_KEYS.GET_BY_ID] })
  queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_BY_TRAVEL_REQUEST] })
}

export function useAddRendicionComprobante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, imageUrl }: { id: string; seccion: RendicionSeccion; imageUrl: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/comprobante`, { imageUrl })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
      showNotification({ title: "IA", message: "Comprobante analizado y agregado", color: "violet" })
    }
  })
}

export function useAddRendicionComprobanteYape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, imageUrl }: { id: string; seccion: RendicionSeccion; imageUrl: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/comprobante-yape`, { imageUrl })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
      showNotification({ title: "IA", message: "Comprobante de Yape/Plin analizado y agregado", color: "violet" })
    }
  })
}

export function useAddRendicionItemComprobante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, imageUrl }: { id: string; seccion: RendicionSeccion; itemId: string; imageUrl: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/comprobante`, { imageUrl })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Comprobante adjuntado", message: "El archivo quedó vinculado al gasto", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo adjuntar", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useAddRendicionPlanillaMovilidadComprobante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, itemId, imageUrl }: { id: string; itemId: string; imageUrl: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/planilla-movilidad/items/${itemId}/comprobante`, { imageUrl })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
      showNotification({ title: "Comprobante adjuntado", message: "El Yape/Plin quedó vinculado a la línea", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo adjuntar", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useAddRendicionItemVoucher() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, imageUrl }: { id: string; seccion: RendicionSeccion; itemId: string; imageUrl: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/voucher`, { imageUrl })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Voucher adjuntado", message: "El voucher quedó vinculado al gasto", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo adjuntar", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useReplaceRendicionSeccion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, items }: { id: string; seccion: RendicionSeccion; items: RendicionItem[] }) => {
      const response = await axios.put(`${API_URL}/rendiciones/${id}/secciones/${seccion}`, { items })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
    }
  })
}

export function useAddRendicionManual() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion }: { id: string; seccion: RendicionSeccion }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/manual`)
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
    }
  })
}

export function useUpdateRendicionContabilidad() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, items }: { id: string; seccion: RendicionSeccion; items: RendicionItem[] }) => {
      const response = await axios.put(`${API_URL}/rendiciones/${id}/${seccion}/contabilidad`, { items })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => invalidateRendicion(queryClient, data.id)
  })
}

export function useExcluirRendicionItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, justificacion }: { id: string; seccion: RendicionSeccion; itemId: string; justificacion?: string }) => {
      const response = await axios.delete(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}`, { data: { justificacion } })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id || (data as any)._id)
      showNotification({ title: "Línea excluida", message: "El gasto fue retirado y el justificativo quedó registrado", color: "green" })
    },
    onError: (error: any) => showNotification({ title: "No se pudo excluir", message: error?.response?.data?.message || "Intenta nuevamente", color: "red" })
  })
}

export function useUpdateRendicionCabecera() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Rendicion["cabecera"]> }) => {
      const response = await axios.put(`${API_URL}/rendiciones/${id}/cabecera`, data)
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_ALL] })
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_BY_TRAVEL_REQUEST] })
    },
    onError: (error: any) => showNotification({ title: "No se pudo guardar", message: error?.response?.data?.message || "Revisa el dato e intenta nuevamente", color: "red" })
  })
}

export function useUpdateRendicionPlanillaMovilidad() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, items }: { id: string; items: RendicionPlanillaMovilidadItem[] }) => {
      const response = await axios.put(`${API_URL}/rendiciones/${id}/planilla-movilidad`, { items })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
    },
    onError: (error: any) => showNotification({
      title: "No se pudo guardar la planilla",
      message: error?.response?.data?.message || error?.message || "Intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useGuardarPlanillaComoMovilizacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/planilla-movilidad/guardar-como-movilizacion`, {})
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
      showNotification({ title: "Plantilla guardada", message: "Se agregó como línea de Movilización", color: "green" })
    },
    onError: (error: any) => showNotification({
      title: "No se pudo guardar la plantilla",
      message: error?.response?.data?.message || error?.message || "Intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useLeerPlanillaMovilidadIA() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, imageUrl }: { id: string; imageUrl: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/planilla-movilidad/leer-ia`, { imageUrl })
      return response.data.data as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
    }
  })
}

export function useUpdateRendicionSustentoTecnico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, items }: { id: string; items: RendicionSustentoTecnicoEntry[] }) => {
      const response = await axios.put(`${API_URL}/rendiciones/${id}/sustento-tecnico`, { items })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => invalidateRendicion(queryClient, data.id)
  })
}

export function useUpdateRendicionDeclaracionJurada() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, items }: { id: string; items: RendicionDeclaracionJuradaItem[] }) => {
      const response = await axios.put(`${API_URL}/rendiciones/${id}/declaracion-jurada`, { items })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => invalidateRendicion(queryClient, data.id)
  })
}

export function useEnviarRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, fechaLiquidacion }: { id: string; fechaLiquidacion?: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/enviar`, { fechaLiquidacion })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Éxito", message: "Rendición enviada a revisión", color: "green" })
    },
    onError: (error: any) => showNotification({
      title: "No se pudo enviar la rendición",
      message: error?.response?.data?.message || error?.message || "Revisa los datos obligatorios e intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useTomarRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/tomar`)
      return response.data.data as Rendicion
    },
    onSuccess: (data, id) => invalidateRendicion(queryClient, data?.id || (data as any)?._id || id),
    onError: (error: any) => showNotification({
      title: "No se pudo tomar la rendición",
      message: error?.response?.data?.message || error?.message || "Intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useAprobarRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, comentario }: { id: string; comentario: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/aprobar`, { comentario })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Éxito", message: `Rendición aprobada · ticket ${data.ticketContableNumero || ""}`, color: "green" })
    },
    onError: (error: any) => showNotification({
      title: "No se pudo aprobar la rendición",
      message: error?.response?.data?.message || error?.message || "Revisa que tengas permiso y que la rendición siga pendiente de revisión",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useRechazarRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, comentario }: { id: string; comentario: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/rechazar`, { comentario })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Rendición rechazada", message: "Se notificó al responsable", color: "orange" })
    },
    onError: (error: any) => showNotification({
      title: "No se pudo rechazar la rendición",
      message: error?.response?.data?.message || error?.message || "Intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useReabrirRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/reabrir`)
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Rendición reabierta", message: "Ya puedes corregirla y reenviarla", color: "blue" })
    },
    onError: (error: any) => showNotification({
      title: "No se pudo reabrir la rendición",
      message: error?.response?.data?.message || error?.message || "Intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}


export function useAnularRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, justificativo }: { id: string; justificativo: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/anular`, { justificativo })
      return response.data.data as Rendicion
    },
    onSuccess: (data) => {
      invalidateRendicion(queryClient, data.id)
      showNotification({ title: "Rendición anulada", message: "La rendición quedó anulada", color: "dark" })
    },
    onError: (error: any) => showNotification({
      title: "No se pudo anular la rendición",
      message: error?.response?.data?.message || error?.message || "Intenta nuevamente",
      color: "red",
      autoClose: 8000
    })
  })
}

export function useGetConsumos(id: string, item?: RendicionItem | null) {
  return useQuery({
    queryKey: ["RENDICION_CONSUMOS", id, item?._id, item?.comprobanteUrl],
    queryFn: async () => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/alimentacion/items/${item!._id}/consumos`)
      return (response.data.data ?? response.data) as RendicionConsumo[]
    },
    enabled: Boolean(item?._id && item.comprobanteUrl && item.consumos === undefined),
    staleTime: Infinity,
    retry: false
  })
}


export function useReleerComprobante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId }: { id: string; seccion: RendicionSeccion; itemId: string }) => {
      const response = await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/releer`)
      return (response.data.data || response.data) as Rendicion
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      invalidateRendicion(queryClient, id)
      queryClient.invalidateQueries({ queryKey: ["RENDICION_CONSUMOS", id] })
    }
  })
}

export interface CatalogosRevision {
  centrosCosto: string[]
  motivosCorreccion: string[]
}

export function useGetCatalogosRevision() {
  return useQuery({
    queryKey: ["RENDICION_CATALOGOS"],
    queryFn: async () => (await axios.get(`${API_URL}/rendiciones/catalogos`)).data.data as CatalogosRevision,
    staleTime: Infinity
  })
}

export interface DecidirComprobanteInput {
  id: string
  seccion: RendicionSeccion
  itemId: string
  estado: ComprobanteDecisionEstado
  motivo?: string
  observacion?: string
  fechaLimite?: string
  version?: number
}

function errorNotification(title: string) {
  return (error: any) => showNotification({ title, message: error?.response?.data?.message || error?.message || "Intenta nuevamente", color: "red", autoClose: 8000 })
}

export function useDecidirComprobante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, ...body }: DecidirComprobanteInput) => (await axios.patch(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/decision`, body)).data.data as Rendicion,
    onSuccess: (data) => invalidateRendicion(queryClient, getRendicionId(data)),
    onError: errorNotification("No se pudo registrar la decisión")
  })
}

export function useRechazarComprobanteContabilidad() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, motivo }: { id: string; seccion: RendicionSeccion; itemId: string; motivo: string }) =>
      (await axios.patch(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/contabilidad-rechazo`, { motivo })).data.data as Rendicion,
    onSuccess: (data, vars) => invalidateRendicion(queryClient, getRendicionId(data) || vars.id),
    onError: errorNotification("No se pudo rechazar el comprobante")
  })
}

export function useAsignarCentroCosto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, index, centroCosto }: { id: string; seccion: RendicionSeccion; itemId: string; index: number; centroCosto: string }) =>
      (await axios.patch(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/personas/${index}/centro-costo`, { centroCosto })).data.data as Rendicion,
    onSuccess: (data) => invalidateRendicion(queryClient, getRendicionId(data)),
    onError: errorNotification("No se pudo asignar el centro de costo")
  })
}

export function useEnviarACorreccion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await axios.post(`${API_URL}/rendiciones/${id}/enviar-correccion`)).data.data as Rendicion,
    onSuccess: (data) => {
      invalidateRendicion(queryClient, getRendicionId(data))
      showNotification({ title: "Enviada a corrección", message: "El responsable podrá corregir los comprobantes marcados", color: "orange" })
    },
    onError: errorNotification("No se pudo enviar a corrección")
  })
}

export function useCorregirComprobante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, item }: { id: string; seccion: RendicionSeccion; itemId: string; item: Partial<RendicionItem> }) =>
      (await axios.patch(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/corregir`, item)).data.data as Rendicion,
    onSuccess: (data) => {
      invalidateRendicion(queryClient, getRendicionId(data))
      showNotification({ title: "Comprobante corregido", message: "Cuando termines con todos, reenvía las correcciones", color: "green" })
    },
    onError: errorNotification("No se pudo corregir el comprobante")
  })
}

export function useReenviarRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await axios.post(`${API_URL}/rendiciones/${id}/reenviar`)).data.data as Rendicion,
    onSuccess: (data) => {
      invalidateRendicion(queryClient, getRendicionId(data))
      showNotification({ title: "Correcciones reenviadas", message: "Administración revisará los comprobantes corregidos", color: "green" })
    },
    onError: errorNotification("No se pudo reenviar")
  })
}

export function useProcesarRendicion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await axios.post(`${API_URL}/rendiciones/${id}/procesar`)).data.data as Rendicion,
    onSuccess: (data) => {
      invalidateRendicion(queryClient, getRendicionId(data))
      queryClient.invalidateQueries({ queryKey: ["RENDICION_CONTABILIDAD"] })
      showNotification({ title: "Rendición procesada", message: "Se registró el procesamiento", color: "green" })
    },
    onError: errorNotification("No se pudo procesar la rendición")
  })
}

export function useGetRendicionesContabilidad(filter: FilterRendicionParams) {
  return useQuery({
    queryKey: ["RENDICION_CONTABILIDAD", filter],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/contabilidad`, { params: filter })
      return { data: (response.data.data ?? []) as Rendicion[], totalRecords: (response.data.totalRecords ?? 0) as number }
    },
    retry: 0
  })
}

export async function buscarComprobanteDuplicado(params: { ruc?: string; tipoDocumento?: string; numero?: string; excluirRendicionId?: string; excluirItemId?: string }) {
  const response = await axios.get(`${API_URL}/rendiciones/duplicado`, { params })
  return (response.data.data ?? null) as { rendicionId: string; codigo: string; responsable?: string; fecha?: string; modulo: string } | null
}

export function useGetRendicionesRevision(filter: FilterRendicionParams) {
  return useQuery({
    queryKey: ["RENDICION_REVISION", filter],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/revision`, { params: filter })
      return { data: (response.data.data ?? []) as Rendicion[], totalRecords: (response.data.totalRecords ?? 0) as number }
    },
    retry: 0
  })
}

export interface MisAccesosRendicion {
  cajaChica: Record<string, boolean>
  tarjetas: Record<string, boolean>
}

/** Qué secciones (caja chica, tarjetas) le corresponden al usuario en cada país, según la configuración. */
export function useGetMisAccesosRendicion() {
  return useQuery({
    queryKey: ["RENDICION_MIS_ACCESOS"],
    queryFn: async () => ((await axios.get(`${API_URL}/rendiciones/mis-accesos`)).data.data ?? { cajaChica: {}, tarjetas: {} }) as MisAccesosRendicion,
    staleTime: 5 * 60_000,
    retry: 0
  })
}

/** Semanas (martes de corte) que se pueden rendir en Caja Chica; Perú es semanal y Ecuador mensual. */
export function useGetCiclosCajaChica(pais: string, enabled: boolean) {
  return useQuery({
    queryKey: ["RENDICION_CICLOS_CAJA", pais],
    queryFn: async () => ((await axios.get(`${API_URL}/rendiciones/caja-chica/ciclos`, { params: { pais } })).data.data ?? { periodicidad: "MENSUAL", ciclos: [] }) as { periodicidad: "SEMANAL" | "MENSUAL"; ciclos: CicloCajaChica[] },
    enabled,
    staleTime: 0,
    retry: 0
  })
}

export function useGetPendientesRevision(enabled: boolean) {
  return useQuery({
    queryKey: ["RENDICION_PENDIENTES_REVISION"],
    queryFn: async () => ((await axios.get(`${API_URL}/rendiciones/revision/pendientes`)).data.data ?? {}) as Record<string, number>,
    enabled,
    refetchInterval: 60_000,
    retry: 0
  })
}

export interface UpdateFormaPagoInput {
  id: string
  anticipo?: boolean
  propios?: boolean
  tarjeta?: boolean
  tarjetaId?: string
  anticipoFecha?: string
  anticipoImporte?: number
  anticipoMoneda?: "PEN" | "USD"
}

export function useUpdateFormaPago() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateFormaPagoInput) => (await axios.put(`${API_URL}/rendiciones/${id}/forma-pago`, body)).data.data as Rendicion,
    onSuccess: (data, { id }) => {
      queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data)
      queryClient.invalidateQueries({ queryKey: [RENDICION_KEYS.GET_ALL] })
    },
    onError: errorNotification("No se pudo guardar la forma de pago")
  })
}

export function useAddPlanillaAdjunto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, imageUrl }: { id: string; imageUrl: string }) => (await axios.post(`${API_URL}/rendiciones/${id}/planilla-movilidad/adjuntos`, { imageUrl })).data.data as Rendicion,
    onSuccess: (data, { id }) => queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data),
    onError: errorNotification("No se pudo adjuntar el sustento")
  })
}

export function useRemovePlanillaAdjunto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string }) => (await axios.delete(`${API_URL}/rendiciones/${id}/planilla-movilidad/adjuntos`, { data: { url } })).data.data as Rendicion,
    onSuccess: (data, { id }) => queryClient.setQueryData([RENDICION_KEYS.GET_BY_ID, id], data),
    onError: errorNotification("No se pudo quitar el adjunto")
  })
}

export interface TarjetaCorporativa {
  id?: string
  _id: string
  pais: string
  titularNombre: string
  banco: string
  ultimos4: string
}

export function useGetMisTarjetas(pais?: string, soloMias = false) {
  return useQuery({
    queryKey: ["TARJETAS_CORPORATIVAS", pais, soloMias],
    queryFn: async () => ((await axios.get(`${API_URL}/tarjetas-corporativas`, { params: { pais, ...(soloMias ? { soloMias: "true" } : {}) } })).data.data ?? []) as TarjetaCorporativa[],
    staleTime: 60_000,
    retry: 0
  })
}

export interface ConsumoTarjeta {
  _id: string
  tarjetaId: string
  fecha: string
  comercio: string
  monto: number
  moneda: string
  referencia: string
  rendicionId?: string | null
  rendicionNumero?: string | null
  bloqueado: boolean
}

export function useGetConsumosTarjeta(tarjetaId?: string) {
  return useQuery({
    queryKey: ["TARJETA_CONSUMOS", tarjetaId],
    queryFn: async () => ((await axios.get(`${API_URL}/tarjetas-corporativas/${tarjetaId}/consumos`)).data.data ?? []) as ConsumoTarjeta[],
    enabled: !!tarjetaId,
    retry: 0
  })
}

export interface CrearTarjetaInput {
  pais: string
  titularNombre: string
  banco: string
  ultimos4: string
}

export function useCrearTarjeta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CrearTarjetaInput) => (await axios.post(`${API_URL}/tarjetas-corporativas`, data)).data.data as TarjetaCorporativa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["TARJETAS_CORPORATIVAS"] })
      showNotification({ title: "Tarjeta registrada", message: "El colaborador ya puede rendir sus consumos", color: "green" })
    },
    onError: errorNotification("No se pudo registrar la tarjeta")
  })
}

export function useDesactivarTarjeta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await axios.delete(`${API_URL}/tarjetas-corporativas/${id}`) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["TARJETAS_CORPORATIVAS"] }),
    onError: errorNotification("No se pudo desactivar la tarjeta")
  })
}

export interface ConsumoInput {
  fecha: string
  comercio: string
  monto: number
  moneda: string
  referencia: string
}

export function useImportarConsumos() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ tarjetaId, consumos }: { tarjetaId: string; consumos: ConsumoInput[] }) =>
      (await axios.post(`${API_URL}/tarjetas-corporativas/${tarjetaId}/consumos`, { consumos })).data.data as { creados: number; omitidos: number },
    onSuccess: (res, { tarjetaId }) => {
      queryClient.invalidateQueries({ queryKey: ["TARJETA_CONSUMOS", tarjetaId] })
      showNotification({ title: "Consumos cargados", message: `${res.creados} nuevos${res.omitidos ? `, ${res.omitidos} ya existían` : ""}`, color: "green" })
    },
    onError: errorNotification("No se pudieron cargar los consumos")
  })
}

export interface NotificacionRendicion {
  _id: string
  rendicionId: string
  numero: string
  evento: string
  asunto: string
  mensaje: string
  detalle: string[]
  leida: boolean
  createdAt: string
}

export function useGetNotificaciones(enabled: boolean) {
  return useQuery({
    queryKey: ["RENDICION_NOTIFICACIONES"],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/rendiciones/notificaciones`)
      return { data: (response.data.data?.data ?? response.data.data ?? []) as NotificacionRendicion[], noLeidas: (response.data.data?.noLeidas ?? response.data.noLeidas ?? 0) as number }
    },
    enabled,
    refetchInterval: 60_000,
    retry: 0
  })
}

export function useMarcarNotificacionesLeidas() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids?: string[]) => { await axios.post(`${API_URL}/rendiciones/notificaciones/leer`, { ids }) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["RENDICION_NOTIFICACIONES"] })
  })
}

export interface MarcarRedFlagInput {
  id: string
  asunto?: string
  descripcion?: string
  cc?: string[]
  desde?: string
}

/** El correo lo envía el módulo de Compliance; esto deja la marca RED FLAG y el historial en la rendición. */
export function useMarcarRedFlag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: MarcarRedFlagInput) => (await axios.post(`${API_URL}/rendiciones/${id}/red-flag`, body)).data.data as Rendicion,
    onSuccess: (data) => invalidateRendicion(queryClient, getRendicionId(data)),
    onError: errorNotification("El red flag se envió, pero no se pudo marcar la rendición")
  })
}

export interface ContabilidadItemInput {
  seccion: RendicionSeccion
  itemId: string
  centroGastos?: string
  registro?: string
  observaciones?: string
}

export function useRegistrarContabilidad() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, items }: { id: string; items: ContabilidadItemInput[] }) => (await axios.put(`${API_URL}/rendiciones/${id}/contabilidad`, { items })).data.data as Rendicion,
    onSuccess: (data) => {
      invalidateRendicion(queryClient, getRendicionId(data))
      showNotification({ title: "Datos contables guardados", message: "Se registró la información de los comprobantes", color: "green" })
    },
    onError: errorNotification("No se pudieron guardar los datos contables")
  })
}

export function useReemplazarComprobanteCorreccion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, seccion, itemId, imageUrl, campo }: { id: string; seccion: RendicionSeccion; itemId: string; imageUrl: string; campo?: "comprobante" | "voucher" }) =>
      (await axios.post(`${API_URL}/rendiciones/${id}/${seccion}/items/${itemId}/comprobante-correccion`, { imageUrl, campo })).data.data as Rendicion,
    onSuccess: (data) => invalidateRendicion(queryClient, getRendicionId(data)),
    onError: errorNotification("No se pudo reemplazar el comprobante")
  })
}

export interface ActualizarTarjetaInput {
  id: string
  titularNombre?: string
  banco?: string
  ultimos4?: string
}

export function useActualizarTarjeta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: ActualizarTarjetaInput) => (await axios.put(`${API_URL}/tarjetas-corporativas/${id}`, body)).data.data as TarjetaCorporativa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["TARJETAS_CORPORATIVAS"] })
      showNotification({ title: "Tarjeta actualizada", message: "Se guardaron los cambios", color: "green" })
    },
    onError: errorNotification("No se pudo actualizar la tarjeta")
  })
}
